import React, { useState, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline } from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { useNavigate } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import { getDynamicTemplateApi } from "../services/dynamic-template.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { getItemApi } from "../services/item.service";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";

const formTypeList = ["Pathology", "Radiology"];

const searchByOptions = [
  { value: "name", label: "Name" },
  { value: "formType", label: "Type" },
  { value: "priority", label: "Priority" },
];

export default function MasterDynamicTemplate() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [item, setItem] = useState(null);
  const [formType, setFormType] = useState("Pathology");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

  const searchByLabel =
    searchByOptions.find((o) => o.value === searchBy)?.label || "Name";

  const { dataList: itemList } = useGetApi({
    apiFunction: getItemApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "items",
  });

  const itemListWithAll = useMemo(
    () => [{ _id: "", itemName: "All" }, ...(itemList || [])],
    [itemList]
  );

  const {
    dataList: dynamicTemplateList,
    isLoading,
    isError,
  } = useGetApi({
    apiFunction: getDynamicTemplateApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      search,
      searchBy,
      item: item?._id,
      formType,
    },
    dependencies: [search, searchBy, item, formType],
    debounceDelay: 500,
    dataKey: "templates",
  });

  const rawRows = useMemo(
    () =>
      (dynamicTemplateList || []).map((row, idx) => ({
        id: row._id || row.id || idx,
        _id: row._id,
        name: row?.name || "-",
        formType: row?.formType || "-",
        priority: row?.priority ?? "-",
        status: row?.status ?? "Active",
        _original: row,
      })),
    [dynamicTemplateList]
  );

  const sortField = sortModel[0]?.field || "name";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = useMemo(
    () =>
      [...rawRows].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        const cmp = String(aVal ?? "").localeCompare(String(bVal ?? ""), undefined, { numeric: true });
        return sortOrder === "asc" ? cmp : -cmp;
      }),
    [rawRows, sortField, sortOrder]
  );
  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows =
    sortedRows.length > pageSize
      ? sortedRows.slice(page * pageSize, page * pageSize + pageSize)
      : sortedRows;

  const handleAddItem = () => navigate("add");

  const getColumns = useCallback(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "formType",
        headerName: "Type",
        flex: 0.8,
        minWidth: 100,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "priority",
        headerName: "Priority",
        flex: 0.5,
        minWidth: 80,
        headerAlign: "center",
        align: "center",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small">
            <RemoveRedEyeOutlinedIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        ),
      },
    ],
    []
  );

  const dataGridSx = {
    border: "none",
    "& .MuiDataGrid-root": { border: "none", width: "100%" },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f0f0f0",
      padding: isMobile ? "4px" : "8px",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold",
    },
    "& .MuiDataGrid-row": {
      "&:hover": { backgroundColor: "#f5f5f5" },
      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
      "&:nth-of-type(even)": { backgroundColor: "white" },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "2px solid #e0e0e0",
      backgroundColor: "#f9f9f9",
    },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#5D6A82",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
              padding: 0,
              transition: "none !important",
              mt: 2,
            }}
          >
            Back
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 2,
            }}
          >
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              Dynamic Template Master
            </Typography>
          </Box>
          <Box
            sx={{
              my: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              paddingBottom: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <TextField
                placeholder={`Search by ${searchByLabel}`}
                size="small"
                sx={{ width: "300px" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <select
                          className="uid-select"
                          value={searchBy}
                          onChange={(e) => setSearchBy(e.target.value)}
                          style={{
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#333",
                            fontWeight: "500",
                          }}
                        >
                          {searchByOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                value={search || ""}
                onChange={(e) => setSearch(e.target.value || "")}
              />
              <Autocomplete
                options={itemListWithAll || []}
                getOptionLabel={(option) => option?.itemName || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Item" size="small" />
                )}
                value={item || null}
                onChange={(_, newValue) => setItem(newValue)}
                sx={{ bgcolor: "white", minWidth: "200px" }}
              />
              <Autocomplete
                options={formTypeList || []}
                renderInput={(params) => (
                  <TextField {...params} label="Type" size="small" />
                )}
                value={formType || ""}
                onChange={(_, newValue) => setFormType(newValue)}
                sx={{ bgcolor: "white", minWidth: "200px" }}
              />
            </Box>
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleAddItem}
            >
              <AddCircleOutline />
              <Typography sx={{ color: "white" }}>New Template</Typography>
            </Button>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: {
                  xs: "calc(100vh - 380px)",
                  sm: "calc(100vh - 400px)",
                  md: "calc(100vh - 420px)",
                },
                width: "100%",
                minHeight: "300px",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                rowCount={sortedRows.length}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                disableRowSelectionOnClick
                sx={dataGridSx}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No templates found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}
